import { useI18nContext } from "@/core/providers/I18nProvider";
import React from "react";
import { Platform, StyleSheet, View, ViewProps } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import BaseHeaderMobile from "./BaseHeaderMobile";

export interface BaseHeaderProps extends Omit<ViewProps, "style"> {
  leading?: React.ReactNode;
  middle?: React.ReactNode;
  trailing?: React.ReactNode;
  style?: ViewProps["style"];
}

export const BaseHeader = React.memo<BaseHeaderProps>(function BaseHeader({
  leading,
  middle,
  trailing,
  style,
  ...props
}) {
  const insets = useSafeAreaInsets();
  const { isRTL } = useI18nContext();

  // On native/mobile, use the simpler mobile header layout provided by the user.
  if (Platform.OS !== "web") {
    return (
      <BaseHeaderMobile
        leading={leading}
        middle={middle}
        trailing={trailing}
        style={style}
        {...props}
      />
    );
  }

  // On web, keep the existing RTL-aware header implementation.
  return (
    <View
      style={[
        styles.container,
        { paddingTop: insets.top + 8 }, // Reduced from 16 to 8 to fix iPhone gap
        style,
      ]}
      {...props}
    >
      {isRTL ? (
        <>
          {/* In RTL, swap leading/trailing so "leading" content appears on the right */}
          <View style={styles.leftSection}>{trailing}</View>
          <View style={styles.centerSection}>{middle}</View>
          <View style={styles.rightSection}>{leading}</View>
        </>
      ) : (
        <>
          <View style={styles.leftSection}>{leading}</View>
          <View style={styles.centerSection}>{middle}</View>
          <View style={styles.rightSection}>{trailing}</View>
        </>
      )}
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    minHeight: 64,
    paddingHorizontal: 16,
    width: "100%", // Make container full width
    position: "relative", // Allow absolute positioning of children
  },
  leftSection: {
    alignItems: "flex-start",
    justifyContent: "center",
    position: "absolute",
    left: 0,
    paddingLeft: 16,
    zIndex: 2,
  },
  centerSection: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    position: "relative", // Allow absolute positioning of children (e.g., logo)
    width: "100%", // Make center section full width
    zIndex: 1,
  },
  rightSection: {
    alignItems: "flex-end",
    justifyContent: "center",
    position: "absolute",
    right: 0,
    paddingRight: 16,
    zIndex: 2,
  },
});

export default BaseHeader;
